import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  listUsers,
  resetUserPassword,
  setUserDisabled,
  updateUser,
  type PlatformRole,
  type UserFilters,
} from '@/api/users';

export function useUsers(filters: UserFilters) {
  return useQuery({
    queryKey: ['users', 'list', filters],
    queryFn: () => listUsers(filters),
    staleTime: 10_000,
  });
}

function useUsersMutation<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', 'list'] });
    },
  });
}

export function useCreateUser() {
  return useUsersMutation(
    (vars: { email: string; displayName?: string; roles: PlatformRole[]; password?: string }) =>
      createUser(vars),
  );
}

export function useUpdateUser() {
  return useUsersMutation((vars: { id: number; roles?: PlatformRole[]; displayName?: string }) =>
    updateUser(vars.id, { roles: vars.roles, displayName: vars.displayName }),
  );
}

export function useResetUserPassword() {
  return useUsersMutation((vars: { id: number; password?: string }) =>
    resetUserPassword(vars.id, vars.password),
  );
}

export function useSetUserDisabled() {
  return useUsersMutation((vars: { id: number; disabled: boolean; reason?: string }) =>
    setUserDisabled(vars.id, vars.disabled, vars.reason),
  );
}
